import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, Package, Plus, LogOut, Loader2, Camera, ImagePlus, X, Gamepad2, ShieldCheck, ArrowDownLeft, ArrowUpRight, PartyPopper, AtSign, Copy, Check, Share2, Link2, QrCode, TrendingUp, Clock, Box, CheckCircle2, Tag, Sparkles, Zap, Leaf, Banknote, Gift } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { StoreRatingBadge } from "@/components/StoreRating";
import KidBellTray from "@/components/KidBellTray";
import ChildLedger from "@/components/ChildLedger";
import BestOfferBadge from "@/components/BestOfferBadge";
import ListingBadges from "@/components/ListingBadges";
import { subscribeListingUpdates, mergeListingInto } from "@/lib/listingBus";
import { subscribeWalletUpdates } from "@/lib/walletBus";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import KidCelebrationModal from "@/components/KidCelebrationModal";
import api, { formatApiError } from "@/lib/api";
import { useKidAuth } from "@/context/KidAuthContext";
import {
  formatGBP,
  conditionLabel,
  CONDITIONS,
  CATEGORIES,
  LISTING_TYPE_SLUGS,
  SAFE_TAG_CHIPS,
  KID_TITLE_CHIPS,
} from "@/lib/marketplace";
import { compressImage } from "@/lib/imageCompress";
import ConditionPicker from "@/components/ConditionPicker";
import ClothingPickers from "@/components/ClothingPickers";
import BestOfferPricing from "@/components/BestOfferPricing";

// Kid Zone shares the master CATEGORIES list from `marketplace.js`
// (Feb 2026 refresh: Baby-Toddler / Tiny Traders / Tween Collectors /
// Teens / Free-Giveaways-Exchange / Other) so parents and kids always see
// exactly the same category chips.

function statusBadge(status) {
  const s = (status || "").toLowerCase();
  if (s === "live" || s === "public" || s === "approved")
    return { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "Live" };
  if (s === "sold") return { c: "bg-blue-100 text-blue-800 border-blue-200", l: "Sold" };
  if (s.includes("rejected")) return { c: "bg-rose-100 text-rose-800 border-rose-200", l: "Rejected" };
  return { c: "bg-amber-100 text-amber-800 border-amber-200", l: "Waiting for parent" };
}

const KID_TITLE_LIMIT = 60;

const kidFallbackTitle = (form, textMode) => {
  const ageLabel = CATEGORIES.find((c) => c.value === (form.categories || [])[0])?.label || "Tiny Traders";
  const base = textMode ? "Toy" : (form.title || "Toy");
  return `${base} - ${ageLabel}`.slice(0, KID_TITLE_LIMIT);
};

export default function KidWorkspace() {
  const nav = useNavigate();
  const { kid, logout } = useKidAuth();
  const [wallet, setWallet] = useState(null);
  // Iteration 261 — this child's own Instant Buy (auto-approve) rule.
  const [instantRule, setInstantRule] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsError, setTransactionsError] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [welcomeToast, setWelcomeToast] = useState("");
  // Iteration 132 — Back-in-stock wishlist cards. Pulled from the same
  // kid notifications endpoint the bell uses so the tray and the
  // dashboard card stay in sync.
  const [backInStock, setBackInStock] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [textMode, setTextMode] = useState(false);
  // Kid Zone defaults to text-free chip picks. Text mode optionally adds
  // title + description while keeping both non-blocking.
  const [form, setForm] = useState({
    title: "",
    description: "",
    // Feb 2026 refresh — multi-select age-range chips. First picked =
    // primary category (sent to upstream as `category`), remaining
    // picks (max 2) are sent as `secondary_categories` so a toy that
    // appeals to multiple age bands shows up under every relevant
    // filter. Default seeds Tiny Traders so submit stays valid.
    categories: ["junior_hub"],
    condition: "used_good",
    tags: [],
    price_pence: 0,
    pay_what_you_can: false,
    // Iteration 297 — Best Offer secret floor price (auto-accept threshold).
    floor_pence: 0,
    photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [successFlash, setSuccessFlash] = useState("");

  const openSubmitModal = () => { setTextMode(false); setSubmitErr(""); setModalOpen(true); };
  const closeSubmitModal = () => { if (!submitting) { setModalOpen(false); setTextMode(false); } };
  // Iteration 298 — live guardrail: floor must never exceed list price.
  // Iteration 310 — Free giveaways / Exchange-Swap category picks lock
  // the price to £0.00 and bypass all paid-price validation.
  const isFreeOrSwapPick = (form.categories || []).some((c) => c === "free" || c === "exchange");
  const floorTooHigh = !isFreeOrSwapPick && form.pay_what_you_can && form.price_pence > 0 && form.floor_pence > form.price_pence;

  // Share Shop UX — Copy handle / Copy shop link / QR code reveal
  const [copiedHandle, setCopiedHandle] = useState(false);
  const [copiedShopLink, setCopiedShopLink] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shopUrl = kid?.kid_id ? `${origin}/shop/${kid.kid_id}` : "";

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch (_e) {
      // Fallback for insecure contexts
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch (_er) { return false; }
    }
  };

  const copyHandle = async () => {
    if (!kid?.handle) return;
    const ok = await copyToClipboard(`@${kid.handle}`);
    if (ok) { setCopiedHandle(true); setTimeout(() => setCopiedHandle(false), 2500); }
  };

  const copyShopLink = async () => {
    if (!shopUrl) return;
    const ok = await copyToClipboard(shopUrl);
    if (ok) { setCopiedShopLink(true); setTimeout(() => setCopiedShopLink(false), 2500); }
  };

  const shareShop = async () => {
    if (!shopUrl) return;
    // Prefer native share sheet on mobile
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${kid?.kid_name || kid?.name || "My"} shop on MyTableTopApp`,
          text: `Come check out my toy shop!`,
          url: shopUrl,
        });
        return;
      } catch (_e) { /* user cancelled or unsupported — fall through to copy */ }
    }
    await copyShopLink();
  };

  useEffect(() => {
    if (kid === null) return;
    if (kid === false || !kid.kid_id) {
      nav("/kidzone", { replace: true });
    }
  }, [kid, nav]);

  // Iteration 138 — first-sale + earnings-milestone celebration modal.
  // Polls `/api/kid/celebrations/next` on every workspace load. The
  // backend returns the highest unseen tier (first-sale → £50 → £100 →
  // £200+) with the most recent sold listing meta. Dismissing calls
  // `/api/kid/celebrations/ack` so each badge only ever pops once.
  const [celebration, setCelebration] = useState(null);
  useEffect(() => {
    if (!kid?.kid_id) return;
    (async () => {
      try {
        const { data } = await api.get("/kid/celebrations/next");
        if (data?.celebration?.badge) setCelebration(data.celebration);
      } catch { /* silent — nothing to celebrate is a valid state */ }
    })();
  }, [kid?.kid_id]);

  // Iteration 299 — payout ping: when the parent marks a cash-out PAID,
  // the backend queues an unseen alert; we pop a celebration card (with
  // confetti) and ack on dismiss so it never replays.
  const [payoutAlert, setPayoutAlert] = useState(null);
  useEffect(() => {
    if (!kid?.kid_id) return;
    (async () => {
      try {
        const { data } = await api.get("/kid/payout-alerts/next");
        if (data?.alert) {
          setPayoutAlert(data.alert);
          import("canvas-confetti").then(({ default: confetti }) => {
            const brand = ["#FF85A1", "#20B2AA", "#FFD166", "#118AB2"];
            confetti({ particleCount: 80, spread: 70, origin: { y: 0.65 }, colors: brand });
          }).catch(() => { /* confetti optional */ });
        }
      } catch { /* no alert is a valid state */ }
    })();
  }, [kid?.kid_id]);
  const dismissPayoutAlert = async () => {
    const a = payoutAlert;
    setPayoutAlert(null);
    if (!a) return;
    try { await api.post("/kid/payout-alerts/ack", { id: a.id }); } catch { /* silent */ }
  };

  // Iteration 299 — personal eco impact (Eco Hero badge card data).
  const [eco, setEco] = useState(null);

  const load = async () => {
    setLoading(true); setErr(""); setTransactionsError("");
    try {
      const [w, l, s, ecoRes] = await Promise.all([
        api.get("/kid/wallet"),
        api.get("/kid/listings"),
        // Kid Zone live stats banner — wallet + earnings + listing counts in
        // one round-trip. If this errors (e.g. upstream flake) we fall back
        // to deriving counts from the `listings` array below.
        api.get("/kid/stats").catch(() => ({ data: null })),
        // Iteration 299 — Eco Hero badge card data (kid-scoped).
        api.get("/kid/eco-impact").catch(() => ({ data: null })),
      ]);
      setWallet(w.data);
      setStats(s?.data || null);
      setEco(ecoRes?.data || null);
      setListings((l.data || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      // Kid wallet history is optional — if upstream errors, surface it in the
      // ledger card instead of the top-level page error banner.
      try {
        const wh = await api.get("/kid/wallet/history?limit=10");
        setTransactions(wh.data?.transactions || []);
      } catch (he) {
        setTransactionsError(formatApiError(he) || "Couldn't load your recent activity.");
        setTransactions([]);
      }
    } catch (e) {
      setErr(formatApiError(e) || "Could not load your workspace.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (kid?.kid_id) load(); }, [kid?.kid_id]);

  // Iteration 132 — poll kid notifications so the Back-in-Stock cards
  // stay fresh alongside the bell tray.
  useEffect(() => {
    if (!kid?.kid_id) return undefined;
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get("/kid/notifications");
        const unread = (data?.notifications || []).filter((n) => !n.read_at && n.kind === "back_in_stock");
        setBackInStock(unread.slice(0, 6));
      } catch { /* silent */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 45000);
    return () => clearInterval(t);
  }, [kid?.kid_id]);

  // Cross-tab / cross-surface revalidation. Any listing edited by parent
  // or kid (this tab, another tab, the modal) splices the fresh payload
  // into the kid's grid so tags update instantly without a reload.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setListings((prev) => mergeListingInto(prev, updated));
    });
  }, []);

  // Wallet + passbook event bus — a completed sale (from another buyer
  // tab) or a fresh parent deposit should immediately refresh the kid's
  // stats banner + passbook rows + running balance.
  useEffect(() => {
    return subscribeWalletUpdates(() => {
      if (kid?.kid_id) load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid?.kid_id]);

  // Welcome toast — fired once per kid_id per browser (sessionStorage).
  // Shown when a kid signs in on a fresh device that hasn't seen them before.
  useEffect(() => {
    if (!kid?.kid_id) return;
    try {
      const key = `mttApp:kidWelcomed:${kid.kid_id}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
      setWelcomeToast(`Hey ${kid.kid_name || kid.name || "there"}! Welcome to Kid Zone 🎮`);
      setTimeout(() => setWelcomeToast(""), 5000);
    } catch (_e) { /* sessionStorage unavailable */ }
  }, [kid?.kid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Milestone celebration — the first time a kid's wallet crosses one of the
  // magic thresholds (£1, £5, £10, £25, £50, £100), fire a "You've earned…"
  // toast. Tracks the highest milestone seen in localStorage per kid so we
  // never nag on subsequent visits.
  const MILESTONES_GBP = [1, 5, 10, 25, 50, 100];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!kid?.kid_id || !wallet) return;
    const balancePennies = wallet.wallet_pennies || 0;
    if (balancePennies <= 0) return;
    const balanceGBP = balancePennies / 100;
    const key = `mttApp:kidMilestone:${kid.kid_id}`;
    let previous = 0;
    try { previous = Number(localStorage.getItem(key) || "0"); } catch (_e) { /* noop */ }
    const crossed = MILESTONES_GBP.filter((m) => balanceGBP >= m && previous < m);
    if (crossed.length === 0) return;
    const highest = crossed[crossed.length - 1];
    try { localStorage.setItem(key, String(highest)); } catch (_e) { /* noop */ }
    const messages = {
      1: "You've earned your first £1! 🎉 Look at you go!",
      5: "£5 in your Piggy Bank! Your storefront is booming 🐷",
      10: "£10 earned! That's a huge milestone 🏆",
      25: "£25 saved! You're a proper little entrepreneur 🌟",
      50: "£50 earned! Time to celebrate 🥳",
      100: "£100 in your Piggy Bank! Legend status unlocked 👑",
    };
    setWelcomeToast(messages[highest]);
    setTimeout(() => setWelcomeToast(""), 6000);
  }, [kid?.kid_id, wallet?.wallet_pennies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iteration 261 — this child's own Instant Buy (auto-approve) rule,
  // read-only from the kid's perspective.
  useEffect(() => {
    api.get("/kid/auto-approve")
      .then(({ data }) => setInstantRule(data))
      .catch(() => setInstantRule(null));
  }, []);

  // Sold toy celebration — detect the first time each listing transitions
  // to `sold` while the kid is looking at their workspace. Fires a confetti
  // burst + a celebration card. Persists the seen sold-ids in localStorage
  // per kid so refreshing the page doesn't replay the confetti.
  const [soldCelebration, setSoldCelebration] = useState(null); // {title, price}
  useEffect(() => {
    if (!kid?.kid_id || !Array.isArray(listings) || listings.length === 0) return;
    const key = `mttApp:kidSoldSeen:${kid.kid_id}`;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(key) || "[]"); }
    catch (_e) { seen = []; }
    const newlySold = listings.filter((l) => (l.status || "").toLowerCase() === "sold" && !seen.includes(l.id));
    if (newlySold.length === 0) return;
    const item = newlySold[0];
    try { localStorage.setItem(key, JSON.stringify([...seen, ...newlySold.map((l) => l.id)])); }
    catch (_e) { /* noop */ }
    setSoldCelebration({ title: item.title, price: item.price });
    // Confetti burst — imported lazily so the ~11KB bundle is only paid
    // for when a sale actually happens.
    import("canvas-confetti").then(({ default: confetti }) => {
      const brand = ["#FF85A1", "#20B2AA", "#FFD166", "#118AB2"];
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.65 }, colors: brand });
      setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.55 }, colors: brand, angle: 60 }), 200);
      setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.55 }, colors: brand, angle: 120 }), 400);
    }).catch(() => { /* module unavailable — skip visuals, keep the card */ });
  }, [kid?.kid_id, listings]);

  const uploadPhoto = async (files, inputEl) => {
    // Iteration 204 — accept a single File or an array/FileList from
    // the mobile multi-picker (`<input multiple>`). Enforce a 5-photo
    // cap and process them sequentially so we never race the state
    // and drop uploads.
    const list = Array.isArray(files) ? files : (files ? Array.from(files.length !== undefined ? files : [files]) : []);
    if (list.length === 0) return;
    const MAX_PHOTOS = 5;
    const currentCount = (form.photos || []).length;
    const slotsLeft = MAX_PHOTOS - currentCount;
    if (slotsLeft <= 0) {
      setSubmitErr(`You can add up to ${MAX_PHOTOS} photos. Remove one to add another.`);
      if (inputEl) inputEl.value = "";
      return;
    }
    const accepted = list.slice(0, slotsLeft);
    setUploading(true);
    try {
      for (const file of accepted) {
        if (!file) continue;
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed, compressed.name || "photo.jpg");
        const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.data_url] }));
      }
      if (list.length > slotsLeft) {
        setSubmitErr(`Only ${slotsLeft} photo${slotsLeft === 1 ? "" : "s"} added — 5-photo cap reached.`);
      }
    } catch (e) {
      setSubmitErr(formatApiError(e) || "Photo upload failed.");
    } finally {
      setUploading(false);
      // Iteration 120 — reset the input so re-selecting the SAME
      // photo (common when a shot from the camera didn't upload the
      // first time) fires onChange again on mobile Safari / Chrome.
      if (inputEl) inputEl.value = "";
    }
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const toggleTag = (tag) => {
    setForm((f) => {
      const has = (f.tags || []).includes(tag);
      if (has) return { ...f, tags: f.tags.filter((t) => t !== tag) };
      if ((f.tags || []).length >= 5) return f; // enforce max 5 tags
      return { ...f, tags: [...(f.tags || []), tag] };
    });
  };

  const submit = async () => {
    setSubmitErr("");
    if (!Array.isArray(form.categories) || form.categories.length === 0) {
      setSubmitErr("Pick at least one age category for your item.");
      return;
    }
    if (form.photos.length === 0) {
      setSubmitErr("Add at least one photo of your toy.");
      return;
    }
    // Iteration 310 — free giveaways / swap listings skip ALL paid-price
    // validation and lock the price to £0.00.
    const freeItem = isFreeOrSwapPick;
    // Fixed-price path requires a chosen step (unless free/swap).
    if (!freeItem && !form.pay_what_you_can && !(form.price_pence > 0)) {
      setSubmitErr("Tap a price for your toy (or switch to Best Offer).");
      return;
    }
    // Iteration 297 — Best Offer mode: list price + secret floor price,
    // floor strictly below the list price.
    if (!freeItem && form.pay_what_you_can) {
      if (!(form.price_pence > 0)) {
        setSubmitErr("Pick a list price for your Best Offer listing.");
        return;
      }
      if (!(form.floor_pence >= 50)) {
        setSubmitErr("Pick a secret floor price (50p or more) — offers at or above it are accepted instantly.");
        return;
      }
      if (form.floor_pence > form.price_pence) {
        setSubmitErr("Secret floor price cannot be higher than list price");
        return;
      }
    }
    const price = freeItem ? 0 : form.price_pence / 100;
    // Iteration 275 — simple mode sends the RAW chip pick; the backend
    // appends the age label exactly once (the frontend fallback title
    // used to pre-suffix it, producing "X - Tiny Traders - Tiny Traders").
    const title = (textMode && form.title.trim() ? form.title.trim() : (form.title || "")).slice(0, KID_TITLE_LIMIT);
    const description = textMode && form.description.trim()
      ? form.description.trim()
      : [
          title,
          freeItem
            ? (form.categories.includes("exchange") ? "Exchange / Swap" : "Free giveaway")
            : form.pay_what_you_can ? "Best Offer" : `Priced £${price.toFixed(2)}`,
          conditionLabel(form.condition),
          ...(form.tags || []),
        ].filter(Boolean).join(" · ");
    setSubmitting(true);
    try {
      const payload = {
        title,
        description,
        price,
        // Iteration 275 — Clothing is an item type: when picked, the
        // primary category is "clothing" and any age-band picks ride
        // along as secondary categories (max 2 upstream).
        // Iteration 312 — keep "free"/"exchange" picks in secondary when
        // clothing is primary: they're the monetization type and the
        // backend's free-item detection depends on seeing them (only the
        // self-referential "clothing" slug is dropped).
        category: isClothingPick ? "clothing" : form.categories[0],
        secondary_categories: isClothingPick
          ? (form.categories || []).filter((v) => v !== "clothing").slice(0, 2)
          : form.categories.slice(1, 3),
        condition: form.condition,
        tags: form.tags || [],
        pay_what_you_can: freeItem ? false : !!form.pay_what_you_can,
        text_mode: textMode,
        photos: form.photos,
      };
      // Iteration 297 — Best Offer carries the secret floor price.
      if (!freeItem && form.pay_what_you_can && form.floor_pence >= 50) {
        payload.floor_price_pennies = form.floor_pence;
      }
      // Iteration 274 — clothing tap-chip picks (only when the Clothing
      // item type is active; empty picks are dropped).
      if (isClothingPick) {
        if (form.clothing_subcategory) payload.clothing_subcategory = form.clothing_subcategory;
        if (form.clothing_gender) payload.clothing_gender = form.clothing_gender;
        if (form.clothing_size) payload.clothing_size = form.clothing_size;
        if (form.clothing_brand) payload.clothing_brand = form.clothing_brand;
      }
      await api.post("/kid/listings", payload);
      setModalOpen(false);
      setTextMode(false);
      setForm({
        title: "", description: "", categories: ["junior_hub"], condition: "used_good", tags: [],
        price_pence: 0, pay_what_you_can: false, floor_pence: 0, photos: [],
        price_pence: 0, pay_what_you_can: false, photos: [],
        clothing_item: false,
        clothing_subcategory: "", clothing_gender: "", clothing_size: "", clothing_brand: "",
      });
      setSuccessFlash("Sent to your parent for approval! You'll see it appear once they say yes.");
      setTimeout(() => setSuccessFlash(""), 6000);
      await load();
    } catch (e) {
      setSubmitErr(formatApiError(e) || "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Iteration 275 — Clothing is an ITEM TYPE (picked alongside Board
  // Game, LEGO Set, …), not an age category. Simple mode: the "Clothing"
  // title chip. Text mode: the "This is clothing" toggle chip.
  const isClothingPick = (!textMode && form.title === "Clothing") || (textMode && !!form.clothing_item);

  if (kid === null || (kid && !kid.kid_id)) {
    return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center">Loading…</div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brandPink/8 via-white to-brandTeal/8" data-testid="kid-workspace-page">
      <Navbar />

      {/* Iteration 138 — first-sale + milestone celebration */}
      {celebration && (
        <KidCelebrationModal
          badge={celebration.badge}
          lastSold={celebration.last_sold}
          kidAlias={celebration.kid_alias || kid?.kid_name || kid?.name}
          shareUrl={celebration.share_url}
          onClose={() => setCelebration(null)}
        />
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        {/* Header — big friendly avatar + name + prominent @handle + sign out */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="text-5xl leading-none flex-shrink-0" data-testid="kid-avatar">{kid?.avatar_emoji || "🧒"}</div>
            <div className="min-w-0 flex-1">
              <div className="pill bg-brandPink/15 text-brandPink"><Gamepad2 className="w-3 h-3 mr-1" /> Kid Zone</div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-brandSlate mt-1 break-words" data-testid="kid-workspace-heading">
                Hi, {kid?.kid_name || kid?.name}!
              </h1>
              {kid?.handle && (
                <button
                  type="button"
                  onClick={copyHandle}
                  className="mt-2 inline-flex items-center gap-2 px-3.5 py-2 rounded-2xl bg-gradient-to-br from-brandTeal to-brandTealDark text-white shadow-md hover:shadow-lg transition-all active:scale-95 max-w-full"
                  data-testid="kid-handle-badge"
                  title="Copy your handle"
                >
                  <AtSign className="w-4 h-4 opacity-80 flex-shrink-0" />
                  <span className="font-mono font-bold text-lg md:text-xl leading-none" data-testid="kid-handle-badge-value">
                    {kid.handle}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-white/25 px-1.5 py-0.5 rounded-md flex-shrink-0">
                    {copiedHandle ? (<><Check className="w-3 h-3" /> Copied</>) : (<><Copy className="w-3 h-3" /> Copy</>)}
                  </span>
                </button>
              )}
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-500" /> Your parent still approves every listing.
              </div>
              {kid?.kid_id && (
                <div className="mt-2" data-testid="kid-workspace-rating-row">
                  <StoreRatingBadge kidId={kid.kid_id} size={13} />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-start">
            <KidBellTray />
            <button
              type="button"
              onClick={async () => { await logout(); nav("/", { replace: true }); }}
              className="btn-secondary text-sm"
              data-testid="kid-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Share My Shop card — prominent 1-tap sharing (native share sheet
            on mobile), Copy Shop Link, and expandable QR code so buyers can
            scan it in person. */}
        {kid?.kid_id && (
          <div
            className="mt-6 rounded-3xl border border-brandPink/25 bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10 p-5 md:p-6 shadow-sm"
            data-testid="kid-share-shop-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-brandPink text-white flex-shrink-0 shadow-md">
                  <Share2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink">Your storefront</div>
                  <div className="font-display text-lg md:text-xl font-semibold text-brandSlate leading-tight">Share My Shop</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Send this link to family &amp; friends so they can browse everything you&rsquo;re selling.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={shareShop}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-brandPink hover:bg-brandPinkDark text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-95 flex-shrink-0 w-full sm:w-auto"
                data-testid="kid-share-shop-btn"
              >
                <Share2 className="w-4 h-4" /> Share My Shop
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-slate-200 px-3 py-2.5">
              <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <a
                href={shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 truncate font-mono text-xs md:text-sm text-slate-700 hover:text-brandTeal transition"
                data-testid="kid-shop-link-url"
                title={shopUrl}
              >
                {shopUrl}
              </a>
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={copyShopLink}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white text-xs font-bold transition flex-1 sm:flex-none justify-center"
                  data-testid="kid-copy-shop-link-btn"
                >
                  {copiedShopLink ? (<><Check className="w-3.5 h-3.5" /> Copied</>) : (<><Copy className="w-3.5 h-3.5" /> Copy Link</>)}
                </button>
                <button
                  type="button"
                  onClick={() => setQrOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-slate-200 hover:border-brandPink hover:text-brandPink text-slate-700 text-xs font-bold transition flex-1 sm:flex-none justify-center"
                  data-testid="kid-toggle-qr-btn"
                  aria-expanded={qrOpen}
                  aria-controls="kid-shop-qr-panel"
                >
                  <QrCode className="w-3.5 h-3.5" /> {qrOpen ? "Hide QR" : "Show QR"}
                </button>
              </div>
            </div>

            {qrOpen && (
              <div
                id="kid-shop-qr-panel"
                className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 flex flex-col sm:flex-row items-center gap-5"
                data-testid="kid-shop-qr-panel"
              >
                <div className="rounded-2xl bg-white p-3 border border-slate-100 shadow-sm flex-shrink-0">
                  <QRCodeSVG
                    value={shopUrl}
                    size={160}
                    level="M"
                    includeMargin={false}
                    fgColor="#188F89"
                    bgColor="#FFFFFF"
                    data-testid="kid-shop-qr-svg"
                  />
                </div>
                <div className="text-center sm:text-left">
                  <div className="font-display text-lg font-semibold text-brandSlate">Scan to shop</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed max-w-xs">
                    Hold up a phone camera to this QR code — it opens your shop instantly. Great for showing off at school or family events!
                  </p>
                  <div className="mt-2 text-[11px] font-mono text-slate-500 truncate max-w-xs" title={shopUrl}>{shopUrl}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {welcomeToast && (
          <div
            className="fixed top-6 right-6 z-50 rounded-2xl bg-white shadow-2xl border border-brandPink/30 px-5 py-4 flex items-center gap-3 max-w-sm animate-in slide-in-from-top-4"
            data-testid="kid-welcome-toast"
            role="status"
          >
            <PartyPopper className="w-6 h-6 text-brandPink flex-shrink-0" />
            <div className="text-sm font-bold text-brandSlate">{welcomeToast}</div>
          </div>
        )}

        {/* Iteration 132 — Back in Stock! Wishlist alerts. Shown only
            when the kid has unread relist notifications. Each card links
            straight to the listing where the kid can hit "Buy with Piggy
            Bank" (existing checkout flow). */}
        {backInStock.length > 0 && (
          <div className="mt-6" data-testid="kid-back-in-stock-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-2xl bg-brandPink text-white">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">My Wishlist &amp; Alerts</div>
                <div className="text-xs text-slate-500">Toys you asked to be notified about — grab them before someone else does.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {backInStock.map((n) => (
                <Link
                  key={n.id}
                  to={`/listing/${n.listing_id}`}
                  className="rounded-2xl border-2 border-brandPink/40 bg-gradient-to-br from-brandPink/8 via-white to-transparent p-4 hover:border-brandPink transition shadow-sm"
                  data-testid={`kid-back-in-stock-card-${n.id}`}
                >
                  <div className="flex items-center gap-3">
                    {n.listing_thumbnail && (
                      <img src={n.listing_thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brandPink text-white">
                        Back in Stock!
                      </span>
                      <div className="mt-1 font-semibold text-slate-900 truncate">
                        {n.listing_title || "Your saved toy"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span
                      className="inline-flex items-center justify-center gap-2 w-full px-4 py-2 rounded-2xl bg-brandTeal hover:bg-brandTealDark text-white font-bold text-sm shadow-sm transition"
                      data-testid={`kid-back-in-stock-buy-${n.id}`}
                    >
                      <Wallet className="w-4 h-4" /> Buy with Piggy Bank
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {soldCelebration && (
          <div
            className="mt-6 rounded-3xl border-2 border-brandPink/40 bg-gradient-to-br from-brandPink/15 via-white to-brandTeal/15 p-5 md:p-6 shadow-lg relative overflow-hidden animate-in fade-in zoom-in-95 duration-300"
            data-testid="kid-sold-celebration"
            role="status"
          >
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-brandPink/20" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-brandTeal/20" />
            <div className="relative flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brandPink text-white inline-flex items-center justify-center shadow-md flex-shrink-0">
                <PartyPopper className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink">You just made a sale!</div>
                <div className="font-display text-xl md:text-2xl font-semibold text-brandSlate leading-tight mt-0.5">
                  {soldCelebration.title} sold&nbsp;{soldCelebration.price != null ? `for ${formatGBP(soldCelebration.price)}` : ""} &nbsp;🎉
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Nice going! Your Piggy Bank will update the moment your parent approves the sale.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSoldCelebration(null)}
                className="text-slate-400 hover:text-slate-700 p-1 flex-shrink-0"
                aria-label="Dismiss"
                data-testid="kid-sold-celebration-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {payoutAlert && (
          <div
            className="mt-6 rounded-3xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-brandTeal/15 p-5 md:p-6 shadow-lg relative overflow-hidden animate-in fade-in zoom-in-95 duration-300"
            data-testid="kid-payout-celebration"
            role="status"
          >
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-emerald-200/40" />
            <div className="relative flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white inline-flex items-center justify-center shadow-md flex-shrink-0">
                <Banknote className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-700">Payout complete!</div>
                <div className="font-display text-xl md:text-2xl font-semibold text-brandSlate leading-tight mt-0.5" data-testid="kid-payout-celebration-amount">
                  {formatGBP((payoutAlert.amount_pennies || 0) / 100)} paid to you&nbsp;🎉
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Your parent marked your cash-out as paid{payoutAlert.payout_method === "bank" ? " by bank transfer" : " in cash"}. Enjoy your earnings!
                </p>
              </div>
              <button
                type="button"
                onClick={dismissPayoutAlert}
                className="text-slate-400 hover:text-slate-700 p-1 flex-shrink-0"
                aria-label="Dismiss"
                data-testid="kid-payout-celebration-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {successFlash && (
          <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-4 text-sm text-emerald-800" data-testid="kid-success-flash">
            {successFlash}
          </div>
        )}
        {err && (
          <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 px-5 py-4 text-sm text-rose-800" data-testid="kid-load-error">{err}</div>
        )}

        {/* Iteration 78 — 6 interactive stat cards mirroring the Parent
            dashboard. Each card deep-links to /kidzone/ledger?tab=<key>. */}
        {(() => {
          const derived = {
            pending:  listings.filter((l) => (l.status || "").toLowerCase().startsWith("pending") || (l.status || "").toLowerCase() === "awaiting_approval").length,
            live:     listings.filter((l) => ["live", "approved", "active"].includes((l.status || "").toLowerCase())).length,
            reserved: listings.filter((l) => (l.status || "").toLowerCase() === "reserved").length,
            sold:     listings.filter((l) => (l.status || "").toLowerCase() === "sold").length,
          };
          // Iteration 96 — Earnings stat card MUST show sale-only totals
          // (never the wallet_pennies balance, which includes parent top-
          // ups + allowance deposits + peer transfers). Backend
          // `kid_stats.total_earnings_pennies` already filters by
          // `_KID_KIND_SALE` — falling back to `wallet.wallet_pennies`
          // used to leak top-ups into the "Earnings" figure and it also
          // caused a stat-card vs Ledger EarningsTab mismatch.
          const earningsPence = stats?.total_earnings_pennies ?? 0;
          const pending  = stats?.pending_approvals ?? derived.pending;
          const live     = stats?.live_listings ?? derived.live;
          const reserved = derived.reserved;
          const sold     = stats?.sold_listings ?? derived.sold;
          // Iteration 96b — the Orders card now counts sold + bought so
          // tapping it lands on a tab that renders the same total. The
          // OrdersTab renders both sold and bought rows, so counting
          // only sold_listings left users staring at "Orders: 0" while
          // 10 BOUGHT rows sat in the tab it deep-links to.
          const bought   = stats?.bought_orders ?? 0;
          const orders   = sold + bought;
          const cards = [
            { k: "pending",  t: "Pending",  v: pending,  a: "bg-orange-50 border-orange-200 text-orange-900",   i: <Clock className="w-4 h-4" /> },
            { k: "live",     t: "Live",     v: live,     a: "bg-emerald-50 border-emerald-200 text-emerald-900", i: <Box className="w-4 h-4" /> },
            { k: "reserved", t: "Reserved", v: reserved, a: "bg-amber-50 border-amber-200 text-amber-900",       i: <TrendingUp className="w-4 h-4" /> },
            { k: "orders",   t: "Orders",   v: orders,   a: "bg-sky-50 border-sky-200 text-sky-900",             i: <CheckCircle2 className="w-4 h-4" /> },
            { k: "earnings", t: "Earnings", v: formatGBP(earningsPence / 100), a: "bg-emerald-50 border-emerald-200 text-emerald-900", i: <Wallet className="w-4 h-4" /> },
            { k: "history",  t: "History",  v: "View",   a: "bg-slate-50 border-slate-200 text-slate-800",       i: <TrendingUp className="w-4 h-4" /> },
          ];
          return (
            <div className="mt-8 rounded-3xl border border-brandPink/25 bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10 p-4 md:p-5" data-testid="kid-live-stats-banner">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-brandPinkDark" />
                <div className="text-xs font-bold uppercase tracking-wider text-brandPinkDark">Your shop &middot; tap any card for the full ledger</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {cards.map((c) => {
                  // Iteration 175 — Unified Orders spec. Orders stat
                  // card routes to `/orders?kid=<id>` so the master
                  // view surfaces the correct kid back-link.
                  const href = c.k === "orders"
                    ? `/orders?kid=${encodeURIComponent(kid?.kid_id || "")}`
                    : `/kidzone/ledger?tab=${c.k}`;
                  return (
                  <Link
                    key={c.k}
                    to={href}
                    className={`rounded-2xl border p-3 hover:shadow-md hover:scale-[1.02] transition-all ${c.a}`}
                    data-testid={`kid-stat-${c.k}`}
                    aria-label={`Open ${c.t}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider">{c.t}</span>
                      {c.i}
                    </div>
                    <div className="text-xl md:text-2xl font-display font-semibold mt-1">{c.v}</div>
                  </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Wallet + submit CTA */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="card-base p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-100 md:col-span-1" data-testid="kid-wallet-card">
            <div className="text-xs text-emerald-800 font-bold uppercase tracking-wider flex items-center gap-1"><Wallet className="w-3 h-3" /> Your wallet</div>
            <div className="text-4xl font-display font-semibold text-emerald-900 mt-2" data-testid="kid-wallet-balance">
              {wallet ? formatGBP((wallet.wallet_pennies || 0) / 100) : "—"}
            </div>
            <div className="text-xs text-slate-500 mt-1">Earnings from sold toys land here after your parent approves each order.</div>
            {instantRule && instantRule.enabled ? (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] font-bold text-amber-800" data-testid="kid-instant-buy-limit">
                <Zap className="w-3 h-3" /> Instant Buy Limit: Up to {formatGBP((instantRule.threshold_pennies || 0) / 100)} (Total cost including fee)
              </div>
            ) : instantRule ? (
              <div className="mt-2 text-[11px] text-slate-400 font-semibold" data-testid="kid-instant-buy-off">
                Instant Buy off — every purchase needs parent approval
              </div>
            ) : null}
          </div>

          <div className="card-base p-5 md:col-span-2 flex items-center justify-between gap-4 flex-wrap" data-testid="kid-submit-cta">
            <div className="min-w-[220px] flex-1">
              <div className="font-display text-lg font-semibold text-brandSlate">Ready to sell another toy?</div>
              <p className="text-sm text-slate-600 mt-1">
                Add a photo, write a friendly description, and pick a fair price. Your parent will approve it before it goes live.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/browse"
                className="btn-secondary inline-flex items-center gap-1.5"
                data-testid="kid-browse-shop-btn"
                title="Browse the marketplace to spend your wallet"
              >
                <Package className="w-4 h-4" /> Browse Shop
              </Link>
              <button
                type="button"
                onClick={openSubmitModal}
                className="btn-primary inline-flex items-center gap-1.5"
                data-testid="kid-open-submit-modal-btn"
              >
                <Plus className="w-4 h-4" /> New listing
              </button>
            </div>
          </div>
        </div>

        {/* Iteration 299 — personal Eco Hero badge card: kg diverted
            from landfill by THIS kid's completed sales + progress to
            the next badge tier. */}
        {eco && (
          <div className="card-base mt-4 p-5 bg-gradient-to-r from-lime-50 via-white to-emerald-50 border-lime-200" data-testid="kid-eco-badge-card">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-12 h-12 rounded-2xl bg-lime-500 text-white inline-flex items-center justify-center shadow-md flex-shrink-0">
                <Leaf className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-lime-700">My Eco Hero badge</div>
                <div className="font-display text-2xl font-semibold text-brandSlate leading-tight" data-testid="kid-eco-weight">
                  {(eco.weight_diverted_kg ?? 0).toFixed(1)} kg diverted from landfill
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-testid="kid-eco-items">
                  {eco.items_rehomed ?? 0} item{(eco.items_rehomed ?? 0) === 1 ? "" : "s"} rehomed
                  {eco.current_badge ? ` · ${eco.current_badge.label} unlocked` : " · sell a toy to unlock your first badge"}
                </div>
              </div>
              <div className="min-w-[160px] flex-1" data-testid="kid-eco-next">
                {eco.next_badge ? (
                  <>
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <span>Next: {eco.next_badge.label}</span>
                      <span>{eco.next_badge.threshold_kg} kg</span>
                    </div>
                    <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-lime-400 to-emerald-500 transition-all"
                        style={{ width: `${Math.min(100, Math.round(((eco.weight_diverted_kg || 0) / eco.next_badge.threshold_kg) * 100))}%` }}
                        data-testid="kid-eco-progress"
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-xs font-bold text-emerald-700">Top badge unlocked — Planet Hero! 🌍</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent transactions mini-ledger */}
        {transactionsError && (
          <div className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-xs text-rose-700" data-testid="kid-transactions-error" role="alert">
            {transactionsError}
          </div>
        )}

        <ChildLedger endpoint="/kid/ledger" title="My passbook" testIdPrefix="kid-workspace-ledger" />

        {transactions.length > 0 && (
          <div className="card-base p-5 mt-4" data-testid="kid-transactions-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold text-brandSlate flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" /> Recent activity
              </h3>
              <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">Last {transactions.length}</span>
            </div>
            <ul className="divide-y divide-slate-100" data-testid="kid-transactions-list">
              {transactions.slice(0, 10).map((t) => {
                // Iteration 125 — sign inversion fix. Previously this
                // treated any row with `amount_pennies > 0` as a
                // credit, but the upstream returns the ABSOLUTE amount
                // for purchases (using the `direction` field to
                // indicate debit). That flipped Tom's purchase rows to
                // display "+£3.30" instead of "-£3.30".
                //
                // Priority order for deciding credit vs debit:
                //   1. Explicit `direction` field ("credit"/"debit"/etc.)
                //   2. Kind → purchase / outgoing-to-parent /
                //      outgoing-to-kid  → debit
                //      sale / credit / parent-to-kid / incoming → credit
                //   3. Fall back to the signed amount as a last resort.
                const kindLc = (t.kind || t.type || "").toString().toLowerCase();
                const directionLc = (t.direction || "").toString().toLowerCase();
                const PURCHASE_KINDS = new Set([
                  "purchase", "kid_purchase", "child_purchase",
                  "piggy_bank_purchase", "item_bought", "buyer_debit",
                ]);
                const OUTGOING_KINDS = new Set([
                  "kid_to_parent", "sent_to_parent",
                  "kid_to_kid", "peer_transfer", "kid_transfer",
                ]);
                const SALE_KINDS = new Set([
                  "sale", "kid_credit", "sold", "listing_sold",
                  "item_sold", "earnings", "payout", "sale_completed",
                  "credit", "sold_credit", "item_payout", "seller_credit",
                  "sale_credit", "listing_payout", "marketplace_sale",
                  "toy_sold", "parent_to_kid", "topup", "top_up",
                ]);
                let isCredit;
                if (["credit", "in", "+", "incoming"].includes(directionLc)) {
                  isCredit = true;
                } else if (["debit", "out", "-", "outgoing"].includes(directionLc)) {
                  isCredit = false;
                } else if (PURCHASE_KINDS.has(kindLc) || OUTGOING_KINDS.has(kindLc)) {
                  isCredit = false;
                } else if (SALE_KINDS.has(kindLc)) {
                  isCredit = true;
                } else {
                  isCredit = (t.amount_pennies || 0) >= 0;
                }
                const amt = Math.abs(t.amount_pennies || 0) / 100;
                return (
                  <li key={t.id} className="py-2.5 flex items-center gap-3" data-testid={`kid-tx-${t.id}`}>
                    <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {isCredit ? <ArrowDownLeft className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{t.note || (t.kind || "").replace(/_/g, " ") || "Transaction"}</div>
                      <div className="text-[11px] text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : ""}</div>
                    </div>
                    <div
                      className={`text-sm font-mono font-bold ${isCredit ? "text-emerald-700" : "text-slate-700"}`}
                      data-testid={`kid-tx-amount-${t.id}`}
                    >
                      {isCredit ? "+" : "-"}{formatGBP(amt)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Listings grid */}
        <h2 className="font-display text-2xl font-semibold text-brandSlate mt-10 mb-3 flex items-center gap-2" data-testid="kid-listings-heading">
          <Package className="w-5 h-5" /> Your listings
        </h2>
        {loading && (
          <div className="text-center py-10 text-slate-500" data-testid="kid-listings-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading your toys…
          </div>
        )}
        {!loading && listings.length === 0 && (
          <div className="card-base p-10 text-center" data-testid="kid-listings-empty">
            <div className="text-4xl mb-3">📦</div>
            <div className="font-bold text-slate-700">You haven&rsquo;t listed any toys yet</div>
            <div className="text-sm text-slate-500 mt-1">Tap &ldquo;New listing&rdquo; above to add your first one!</div>
          </div>
        )}
        {!loading && listings.length > 0 && (() => {
          const pending = listings.filter((l) => {
            const s = (l.status || "").toLowerCase();
            return s === "pending_parent" || s === "pending_parent_review" || s === "pending_ai";
          });
          if (pending.length === 0) return null;
          return (
            <div
              className="mt-4 rounded-3xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-brandPink/5 p-5"
              data-testid="kid-pending-requests-panel"
            >
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-amber-400 text-white shadow-sm">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700">Waiting for parent</div>
                  <div className="font-display text-lg font-semibold text-brandSlate leading-tight">
                    {pending.length} listing{pending.length === 1 ? "" : "s"} pending approval
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-2" data-testid="kid-pending-requests-list">
                {pending.slice(0, 5).map((p) => (
                  <li
                    key={p.id}
                    className="rounded-2xl bg-white border border-amber-100 px-3 py-2 flex items-center gap-3"
                    data-testid={`kid-pending-listing-${p.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex-shrink-0 overflow-hidden">
                      {p.photos?.[0] ? (
                        <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm text-slate-900 truncate">{p.title}</div>
                      <div className="text-[11px] text-slate-500">{formatGBP(p.price)} &middot; awaiting parent OK</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {!loading && listings.length > 0 && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4" data-testid="kid-listings-grid">
            {listings.map((it) => {
              const b = statusBadge(it.status);
              return (
                <div key={it.id} className="card-base overflow-hidden" data-testid={`kid-listing-${it.id}`}>
                  <div className="aspect-square bg-slate-100 relative">
                    {it.photos?.[0] ? (
                      <img src={it.photos[0]} alt={it.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl">🎁</div>
                    )}
                    <span className={`absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${b.c}`} data-testid={`kid-listing-status-${it.id}`}>
                      {b.l}
                    </span>
                    {it.pay_what_you_can && (
                      <div className="absolute top-2 right-2">
                        <BestOfferBadge size="sm" testId={`kid-workspace-best-offer-${it.id}`} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-slate-900 line-clamp-1">{it.title}</div>
                    <div className="mt-1 flex items-center justify-between">
                      <div className="text-xl font-display font-semibold text-blue-600">{formatGBP(it.price)}</div>
                      <div className="text-[11px] text-slate-500">{conditionLabel(it.condition)}</div>
                    </div>
                    {/* Iteration 86 — unified badge stack so Kid Zone
                        renders BULKY / COLLECTION ONLY / POSTED BY PARENT
                        identically to Parent Dashboards & Browse. */}
                    <ListingBadges
                      listing={it}
                      only={["parcel", "parent_posted"]}
                      className="mt-2"
                      testid={`kid-listing-badges-${it.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Submit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={closeSubmitModal} data-testid="kid-submit-modal">
          <div className="relative bg-white rounded-2xl max-w-lg w-full p-6 md:p-7 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {submitting && (
              <div
                className="absolute inset-0 z-10 rounded-2xl bg-white/85 backdrop-blur-sm flex flex-col items-center justify-center gap-4 px-6 text-center"
                data-testid="kid-submit-progress-overlay"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="relative w-20 h-20">
                  <span className="absolute inset-0 rounded-full border-4 border-brandPink/20" />
                  <span className="absolute inset-0 rounded-full border-4 border-brandPink border-t-transparent animate-spin" />
                  <span className="absolute inset-2 rounded-full bg-gradient-to-br from-brandPink/15 to-brandTeal/15 flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-brandTeal" />
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div className="font-display text-lg font-semibold text-brandSlate">
                    Our safety helper is checking your photos…
                  </div>
                  <div className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                    Give us a few seconds while we make sure everything looks great, then it'll go straight to your parent for approval.
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-1" aria-hidden="true">
                  <span className="w-1.5 h-1.5 rounded-full bg-brandPink animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-brandTeal animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                  New listing · {textMode ? "add title & text" : "simple / text-free"}
                </div>
                <div className="font-display text-xl font-semibold text-brandSlate" data-testid="kid-submit-modal-title">{isClothingPick ? "Pick your clothing details" : "Pick your item details"}</div>
              </div>
              <button type="button" onClick={closeSubmitModal} disabled={submitting} className="text-slate-400 hover:text-slate-700" aria-label="Close" data-testid="kid-submit-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 rounded-2xl bg-slate-100 p-1" data-testid="kid-text-mode-toggle">
              <button
                type="button"
                onClick={() => { setTextMode(false); setForm({ ...form, title: "", description: "" }); }}
                className={`rounded-xl px-3 py-2 text-xs font-bold transition ${!textMode ? "bg-white text-brandSlate shadow-sm" : "text-slate-500 hover:text-brandSlate"}`}
                data-testid="kid-text-mode-simple"
                aria-pressed={!textMode}
              >
                Simple / Text-Free
              </button>
              <button
                type="button"
                onClick={() => { setTextMode(true); setForm({ ...form, title: "", description: "" }); }}
                className={`rounded-xl px-3 py-2 text-xs font-bold transition ${textMode ? "bg-brandPink text-white shadow-sm" : "text-slate-500 hover:text-brandPink"}`}
                data-testid="kid-text-mode-text"
                aria-pressed={textMode}
              >
                Add Title &amp; Description
              </button>
            </div>
            <div className="mt-5 space-y-5">

              {/* Photos */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Photos (up to 5)</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                      <img src={p} alt={`upload ${i}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow" aria-label="Remove photo" data-testid={`kid-remove-photo-${i}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {form.photos.length < 5 && (
                    <label className="aspect-square rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-brandPink hover:text-brandPink cursor-pointer transition">
                      {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                      <span className="text-[11px] font-bold">{uploading ? "Compressing…" : "Add photo"}</span>
                      {/* Iteration 120 — no `capture=` attribute so mobile
                          Safari / Chrome present the full OS media sheet
                          ("Photo Library", "Take Photo", "Choose File")
                          instead of forcing the camera open. Kids can
                          still take a fresh photo, but adults can now
                          pick pre-shot ones from the gallery too. */}
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhoto(e.target.files, e.target)} data-testid="kid-photo-input" />
                    </label>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Photos are shrunk to 1200px on your phone before we send them, so uploads stay quick.</div>
              </div>

              {!textMode ? (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">What kind of item is it?</label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="kid-form-title-chips">
                    {KID_TITLE_CHIPS.map((label) => {
                      const picked = form.title === label;
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setForm({
                            ...form,
                            title: label,
                            // Iteration 275 — leaving the Clothing item
                            // type clears its attribute picks.
                            ...(label !== "Clothing" ? {
                              clothing_subcategory: "", clothing_gender: "",
                              clothing_size: "", clothing_brand: "",
                            } : {}),
                          })}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${picked ? "bg-brandPink text-white border-brandPink" : "bg-white text-slate-700 border-slate-200 hover:border-brandPink"}`}
                          data-testid={`kid-form-title-chip-${label.replace(/\s+/g, "-").toLowerCase()}`}
                          aria-pressed={picked}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500" data-testid="kid-simple-fallback-hint">
                    We&apos;ll name it automatically from your picks, like <strong>{kidFallbackTitle(form, false)}</strong>.
                  </p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="kid-text-fields">
                  {/* Iteration 275 — text mode has no item-type chips, so
                      clothing gets its own tap toggle here. */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        clothing_item: !form.clothing_item,
                        ...(form.clothing_item ? {
                          clothing_subcategory: "", clothing_gender: "",
                          clothing_size: "", clothing_brand: "",
                        } : {}),
                      })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${form.clothing_item ? "bg-brandPink text-white border-brandPink" : "bg-white text-slate-700 border-slate-200 hover:border-brandPink"}`}
                      data-testid="kid-form-clothing-toggle"
                      aria-pressed={!!form.clothing_item}
                    >
                      This is a clothing item
                    </button>
                  </div>
                  <div>
                    <label htmlFor="kid-form-title" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Listing Title <span className="text-slate-400 normal-case">(Optional)</span>
                    </label>
                    <input
                      id="kid-form-title"
                      type="text"
                      value={form.title}
                      maxLength={KID_TITLE_LIMIT}
                      onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, KID_TITLE_LIMIT) })}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:border-brandPink focus:ring-2 focus:ring-brandPink/20 outline-none"
                      placeholder="Add a short title, or leave it blank"
                      data-testid="kid-form-title-input"
                    />
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <span className="text-[11px] text-slate-500">Blank is OK — we&apos;ll use <strong>{kidFallbackTitle(form, true)}</strong>.</span>
                      <span className="text-[11px] font-bold text-slate-500" data-testid="kid-form-title-counter">{form.title.length}/{KID_TITLE_LIMIT}</span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="kid-form-description" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Description <span className="text-slate-400 normal-case">(Optional)</span>
                    </label>
                    <textarea
                      id="kid-form-description"
                      rows={3}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:border-brandPink focus:ring-2 focus:ring-brandPink/20 outline-none resize-none"
                      placeholder="Add a friendly note about the toy, or leave it blank"
                      data-testid="kid-form-description-input"
                    />
                  </div>
                </div>
              )}

              {/* Category chips — multi-select age ranges (Feb 2026 refresh).
                  Kids can pick up to 3 age bands for toys that appeal to
                  more than one age group. "Free" and "Other" are
                  mutually-exclusive shortcuts that clear the age picks. */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Age category <span className="text-slate-400 font-normal">· pick up to 3</span>
                  </label>
                  <span className="text-[11px] text-slate-500" data-testid="kid-form-categories-count">
                    {(form.categories || []).length}/3
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="kid-form-category-chips">
                  {/* Iteration 275 — Clothing is an item TYPE (top chip
                      row), not an age category, so it's filtered out of
                      this row entirely. */}
                  {CATEGORIES.filter((c) => c.value !== "clothing").map((c) => {
                    // Iteration 173 — listing-type slugs (Free giveaways /
                    // Exchange / Other) are mutually-exclusive with age
                    // bands AND with each other so the picker mirrors
                    // the Browse filter chips exactly.
                    const isSpecial = LISTING_TYPE_SLUGS.has(c.value);
                    const picked = (form.categories || []).includes(c.value);
                    const atMax = !picked && !isSpecial && (form.categories || []).length >= 3;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => {
                          const current = form.categories || [];
                          if (isSpecial) {
                            // Picking a listing-type slug clears every
                            // other selection and sets just this one;
                            // unpicking falls back to the default age
                            // band so the form stays valid.
                            setForm({
                              ...form,
                              categories: picked ? ["junior_hub"] : [c.value],
                            });
                            return;
                          }
                          if (picked) {
                            const next = current.filter((v) => v !== c.value);
                            // Must keep at least one — snap back to default.
                            setForm({ ...form, categories: next.length ? next : ["junior_hub"] });
                          } else {
                            // Picking an age chip clears any listing-type slug.
                            const ageOnly = current.filter(
                              (v) => !LISTING_TYPE_SLUGS.has(v),
                            );
                            if (ageOnly.length >= 3) return;
                            setForm({ ...form, categories: [...ageOnly, c.value] });
                          }
                        }}
                        disabled={atMax}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition disabled:opacity-40 disabled:cursor-not-allowed ${picked ? "bg-brandTeal text-white border-brandTeal" : "bg-white text-slate-700 border-slate-200 hover:border-brandTeal"}`}
                        data-testid={`kid-form-category-chip-${c.value}`}
                        aria-pressed={picked}
                      >
                        {c.label}{c.ageHint ? ` · ${c.ageHint}` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Iteration 274/275 — Clothing tap-chips (Kid Zone stays
                  strictly tap-only: no free text, so nothing needs
                  re-moderation). Shown when the Clothing item type is
                  picked (title chip in simple mode / toggle in text
                  mode). */}
              {isClothingPick && (
                <ClothingPickers
                  testIdPrefix="kid-form-clothing"
                  values={form}
                  onChange={(updates) => setForm((f) => ({ ...f, ...updates }))}
                />
              )}

              {/* Safe tags chips (multi-select up to 5) */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Tags <span className="text-slate-400 font-normal">· pick up to 5</span>
                  </label>
                  <span className="text-[11px] text-slate-500" data-testid="kid-form-tags-count">{(form.tags || []).length}/5</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="kid-form-tag-chips">
                  {SAFE_TAG_CHIPS.map((tag) => {
                    const picked = (form.tags || []).includes(tag);
                    const disabled = !picked && (form.tags || []).length >= 5;
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        disabled={disabled}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${picked ? "bg-amber-400 text-white border-amber-400" : disabled ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-200 hover:border-amber-400"}`}
                        data-testid={`kid-form-tag-chip-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                        aria-pressed={picked}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Condition chips */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Condition</label>
                <div className="mt-2" data-testid="kid-form-condition-chips">
                  <ConditionPicker
                    value={form.condition}
                    onChange={(condition) => setForm({ ...form, condition })}
                    testIdPrefix="kid-form-condition-chip"
                  />
                </div>
              </div>

              {/* Pricing — Fixed vs Pay What You Can.
                  Iteration 310 — Free giveaways / Exchange-Swap picks lock
                  the price to £0.00 and replace the whole paid-price UI. */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Price</label>
                {isFreeOrSwapPick ? (
                  <div className="mt-2 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-center gap-2" data-testid="kid-form-free-price-lock">
                    <Gift className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-bold text-emerald-800" data-testid="kid-form-free-price-label">
                        {form.categories.includes("exchange") ? "Swap Item (£0.00)" : "Free Item (£0.00)"}
                      </div>
                      <div className="text-xs text-emerald-700">No price needed — this one&apos;s on the house. Just tap send!</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mt-2 inline-flex rounded-full border border-slate-200 p-1" data-testid="kid-form-price-mode">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, pay_what_you_can: false })}
                        className={`px-3 py-1 text-xs font-bold rounded-full transition ${!form.pay_what_you_can ? "bg-brandPink text-white" : "text-slate-700"}`}
                        data-testid="kid-form-price-mode-fixed"
                        aria-pressed={!form.pay_what_you_can}
                      >Fixed Price</button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, pay_what_you_can: true, floor_pence: 0 })}
                        className={`px-3 py-1 text-xs font-bold rounded-full transition ${form.pay_what_you_can ? "bg-brandPink text-white" : "text-slate-700"}`}
                        data-testid="kid-form-price-mode-pwyc"
                        aria-pressed={!!form.pay_what_you_can}
                      >Best Offer</button>
                    </div>
                    <BestOfferPricing
                      bestOffer={!!form.pay_what_you_can}
                      pricePence={form.price_pence}
                      floorPence={form.floor_pence}
                      onPriceChange={(p) => setForm((f) => ({ ...f, price_pence: p }))}
                      onFloorChange={(p) => setForm((f) => ({ ...f, floor_pence: p }))}
                      testIdPrefix="kid-form"
                    />
                  </>
                )}
              </div>

              {submitErr && <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="kid-submit-error">{submitErr}</div>}
              <button
                type="button"
                onClick={submit}
                disabled={submitting || uploading || floorTooHigh}
                className="w-full btn-primary disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="kid-submit-final-btn"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Checking safety…</>) : "Send to my parent for approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}


function StatTile({ label, value, icon, accent, testid }) {
  return (
    <div className={`rounded-2xl border px-3 py-3 ${accent}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-2xl md:text-3xl font-display font-semibold mt-1 leading-tight" data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}
